import { readCookie, writeCookie } from './cookie';
import { cookieConfig } from './cookie-config';
import EventEmitter from './eventEmitter';
import { getRootDomain } from './getRootDomain';
import storage from './storage/pulseStorage';
const { PULSE_COOKIE_KEY, ENV_COOKIE_KEY } = cookieConfig;
const CIS_POLL_INTERVAL = 900000; // 15 minutes
/**
 * Responsible for subscribing to, and emitting events.
 */
export const eventEmitter = new EventEmitter();
export function cacheOldEnvironmentId(id) {
    try {
        let envCookie = '';
        // biome-ignore lint/suspicious/noExplicitAny: -
        let oldIds = {};
        try {
            envCookie = readCookie(ENV_COOKIE_KEY);
        }
        catch (_e) { }
        try {
            oldIds = envCookie ? JSON.parse(envCookie) : [];
        }
        catch (_e) { }
        oldIds[id] = Date.now();
        const idsToCache = Object.keys(oldIds)
            .map((envId) => ({ id: envId, date: oldIds[envId] }))
            .sort((o) => o.date)
            .slice(0, 5)
            // biome-ignore lint/performance/noAccumulatingSpread: -
            .reduce((accl, o) => ({ ...accl, [o.id]: o.date }), {});
        writeCookie(ENV_COOKIE_KEY, JSON.stringify(idsToCache), getRootDomain(), 365);
    }
    catch (_e) { }
}
export function parsePulseCookies(cookie = readCookie(PULSE_COOKIE_KEY)) {
    let values = decodeURIComponent(cookie).split(',');
    // Cookie is in an unknown, possibly old format. Will be ignored.
    if (values.length !== 10) {
        values = [];
    }
    const altEnvCookie = readCookie(ENV_COOKIE_KEY);
    // biome-ignore lint/suspicious/noImplicitAnyLet: -
    let altEnvIds;
    if (altEnvIds) {
        try {
            altEnvIds = JSON.parse(altEnvCookie);
        }
        catch (_e) {
            altEnvIds = undefined;
        }
    }
    const ppId = storage.getPPIDsFromStorage();
    const cisCache = storage.getCisCacheFromStorage();
    return {
        environmentId: values[0],
        prevUserId: values[2] ? values[2] : undefined,
        refreshAfterTimestamp: values[3] ? Number.parseInt(values[3], 10) : undefined,
        jwe: values[4],
        anId: undefined,
        syncWithAppNexus: 0,
        doTracking: true,
        pixelInjected: values[8] ? values[8] === 'true' : undefined,
        adId: values[9],
        altEnvIds,
        visitorIdPlaceholder: values[1],
        ppId,
        includeAdvertising: cisCache?.includeAdvertising,
    };
}
export function serializePulseCookie(input) {
    const { environmentId = '', prevUserId = '', jwe = '', pixelInjected = '', adId = '', visitorIdPlaceholder = 'v', ppId = {}, refreshAfterTimestamp, includeAdvertising, } = input || {};
    const now = Date.now();
    const refreshCisAfterTimestamp = refreshAfterTimestamp || new Date(now + CIS_POLL_INTERVAL).getTime();
    const cookieString = [
        environmentId,
        visitorIdPlaceholder,
        prevUserId,
        refreshCisAfterTimestamp,
        jwe,
        '', // anId: not used anymore, but required for cookie payload validity
        0, // syncWithAppNexus: not used anymore, but required for cookie payload validity
        true, // doTracking: not used anymore, but required for cookie payload validity
        pixelInjected,
        adId,
    ].join(',');
    storage.setPPIDsToStorage(ppId);
    storage.setCisCacheToStorage({ includeAdvertising });
    writeCookie(PULSE_COOKIE_KEY, cookieString, getRootDomain(), 2 * 365);
    eventEmitter.emit('environmentIdSet', environmentId);
    eventEmitter.emit('jweSet', jwe);
}
export var CisSyncReason;
(function (CisSyncReason) {
    CisSyncReason["Initialise"] = "initialise";
    CisSyncReason["Login"] = "login";
    CisSyncReason["Logout"] = "logout";
    CisSyncReason["Refresh"] = "refresh";
    CisSyncReason["includeAdvertisingChange"] = "includeAdvertisingChange";
})(CisSyncReason || (CisSyncReason = {}));
/**
 * Clears all relevant advertisement identifiers from cookie storage, returning the new storage state
 * @returns {PulseCookie}
 * @deprecated Advertising Identifiers methods will be removed in the next major version.
 */
export function clearCookieAdvertisingIds() {
    const cookieValues = {
        ...parsePulseCookies(),
        adId: '',
        anId: '',
        includeAdvertising: false,
        jwe: '',
        syncWithAppNexus: 0,
    };
    serializePulseCookie(cookieValues);
    return cookieValues;
}
/**
 * Runs actions related to a new JWE, divergent from the one persisted in the browser storage
 * @param jwe
 *
 * @privateRemarks
 * if there is no jwe, or in other words, no nativeJwe was provided in config, no action will be taken. That's the
 * behaviour with plain Web applications. The scenario not covered here is when we do have a hybrid app, and they don't
 * send any nativeJwe in config.
 * @deprecated Advertising Identifiers methods will be removed in the next major version.
 */
export function clearCookieJweIfStale(jwe = '') {
    const { jwe: currentJwe } = parsePulseCookies();
    if (jwe && currentJwe !== jwe) {
        clearCookieAdvertisingIds();
    }
}
/**
 * Runs actions related to a new JWE, divergent from the one persisted in the browser storage
 * @param refreshAfterDateTime
 */
export function resetRefreshAfter(refreshAfterDateTime) {
    const cookieValues = {
        ...parsePulseCookies(),
        refreshAfterTimestamp: refreshAfterDateTime ? refreshAfterDateTime.getTime() : Date.now(),
    };
    serializePulseCookie(cookieValues);
    return cookieValues;
}
/**
 * Checks whether the login conditions are met.
 * @param userId current user's id
 * @param prevUserId previous user's id stored in the application
 * @returns
 */
const shouldLogin = (userId, prevUserId) => {
    return Boolean(userId) && String(userId) !== String(prevUserId);
};
/**
 * Checks whether the logout conditions are met.
 * @param userId current user's id
 * @param prevUserId previous user's id stored in the application
 * @returns
 */
const shouldLogout = (userId, prevUserId) => {
    return !userId && String(userId) !== String(prevUserId);
};
/**
 * Checks whether the refresh conditions are met.
 * @param currentTimestamp now, expressed as timestamp in ms
 * @param refreshAfterTimestamp threshold, expressed as timestamp in ms
 */
const shouldRefresh = (currentTimestamp, refreshAfterTimestamp) => {
    return refreshAfterTimestamp && currentTimestamp > refreshAfterTimestamp;
};
/**
 * Checks whether privacy context for emitting Advertising Identifiers has changed.
 * @param currentIncludeAdvertising cached value for includeAdvertising
 * @param updatedIncludeAdvertising newly processed value  for includeAdvertising
 * @deprecated Advertising Identifiers methods will be removed in the next major version.
 */
const shouldRefetchAdvertisingIdentifiers = (currentIncludeAdvertising, updatedIncludeAdvertising) => {
    return currentIncludeAdvertising !== updatedIncludeAdvertising;
};
/**
 * Checks whether the initialisation conditions are met.
 * @param cookieInput a raw cookie input, expressing the stored state of the application
 * @param currentCookie a parsed cookie
 * @param includeAdvertising expresses whether advertising ids are expected in the stored state of the application
 * @param vendors for which vendors the application is expecting advertising ids
 */
const shouldInitialise = (cookieInput, currentCookie, includeAdvertising = false, vendors = []) => {
    return (!cookieInput ||
        cookieInput.indexOf('undefined') !== -1 || // 'undefined' should not be a value in the raw cookie. Treat as invalid.
        !currentCookie.environmentId ||
        !currentCookie.jwe ||
        (includeAdvertising && vendors.length > 0 && Object.keys(currentCookie.ppId).length === 0));
};
/**
 * Determines whether CIS should be called to update identity values.
 * @param userId
 * @param options
 * @returns {SyncState}
 */
export function getSyncState(userId, options = {}) {
    const { cookie: cookieInput = readCookie(PULSE_COOKIE_KEY), includeAdvertising, vendors } = options;
    const currentCookie = parsePulseCookies(cookieInput);
    const currentTimestamp = Date.now();
    /*
     * This case only handles login.  For example when we don't necessarily have an event (tracker.update({actor: ...})
     * might or might not have been called), then we only want to sync if we DO have a userId, and if that userId is not
     * in our cookie.
     * Blocking call on login/new user to get opt out status and on logout, as SPAs rely on that state to be accurate
     * on all PPID methods after updates.
     */
    if (shouldLogin(userId, currentCookie.prevUserId)) {
        return {
            syncWithCis: true,
            waitForCis: true,
            syncWithCisReason: CisSyncReason.Login,
        };
    }
    /*
     * Blocking call on login/new user to get opt out status and on logout, as SPAs rely on that state to be accurate
     * on all PPID methods after updates.
     */
    if (shouldLogout(userId, currentCookie.prevUserId)) {
        return {
            syncWithCis: true,
            waitForCis: true,
            syncWithCisReason: CisSyncReason.Logout,
        };
    }
    /*
     * Blocking call on login/new user to get opt out status, non-blocking call on logout to get a new JWE.
     */
    if (shouldRefresh(currentTimestamp, currentCookie.refreshAfterTimestamp)) {
        return {
            syncWithCis: true,
            waitForCis: true,
            syncWithCisReason: CisSyncReason.Refresh,
        };
    }
    if (shouldInitialise(cookieInput, currentCookie, includeAdvertising, vendors)) {
        return {
            syncWithCis: true,
            waitForCis: true,
            syncWithCisReason: CisSyncReason.Initialise,
        };
    }
    if (shouldRefetchAdvertisingIdentifiers(currentCookie.includeAdvertising, includeAdvertising)) {
        return {
            syncWithCis: true,
            waitForCis: true,
            syncWithCisReason: CisSyncReason.includeAdvertisingChange,
        };
    }
    return {
        syncWithCis: false,
    };
}
//# sourceMappingURL=pulse-cookie.js.map